import React from "react";
import Button from "../../../shared/components/UI/Button";
import Input from "../../../shared/components/UI/Input";
import Form from "../../../shared/components/UI/Form";
import Navbar from "../../../shared/components/navbar/NavBar";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import authService from "../services/authService";
import { AxiosError } from "axios";
import { displayNotify } from "../../../shared/services/Notifications/NotificationServices";
import "./LoginForm.scss";

interface IForm {
  username: string;
  password: string;
}

interface Props {
  isRegister: boolean;
  setIsRegister: React.Dispatch<React.SetStateAction<boolean>>;
}

const loginNotificationMap: Record<
  number,
  { title: string; description: string }
> = {
  401: {
    title: "Ошибка входа",
    description: "Неверный логин или пароль",
  },
  403: {
    title: "Аккаунт заблокирован",
    description: "Обратитесь к администратору для разблокировки.",
  },
  422: {
    title: "Ошибка валидации",
    description: "Некорректные данные. Проверьте форму.",
  },
  429: {
    title: "Слишком много попыток",
    description: "Подождите немного перед следующей попыткой входа.",
  },
  500: {
    title: "Ошибка сервера",
    description: "Что-то пошло не так. Попробуйте позже.",
  },
};

function LoginForm({ isRegister, setIsRegister }: Props) {
  const navigate = useNavigate();
  const { register, handleSubmit, getValues, trigger } = useForm<IForm>({
    mode: "onChange",
  });

  const loginM = useMutation({
    mutationFn: (form: IForm) => authService.authFetch(form),
    onSuccess: () => {
      displayNotify({
        status: "success",
        title: "Успешный вход",
        description: "Вы успешно вошли в систему",
      });
      navigate("/");
    },
    onError: (error: AxiosError) => {
      if (!error.response) {
        displayNotify({
          status: "error",
          title: "Нет соединения",
          description: "Проверьте интернет или попробуйте позже.",
        });
        return;
      }

      const status = error.response.status;
      const detail = (error.response.data as any)?.detail;

      if (detail?.includes("CSRF Failed")) {
        displayNotify({
          status: "error",
          title: "Ошибка безопасности",
          description: "CSRF ошибка. Обновите страницу и попробуйте снова.",
        });
        return;
      }

      if (typeof detail === "string" && detail.includes("Уже авторизован")) {
        displayNotify({
          status: "error",
          title: "Вы уже в системе",
          description:
            "Вы уже вошли ранее. Обновите страницу, если нужно повторно войти.",
        });
        return;
      }

      const message = loginNotificationMap[status];

      displayNotify({
        status: "error",
        title: message?.title || "Неизвестная ошибка",
        description:
          message?.description || detail || "Попробуйте снова позже.",
      });
    },
  });

  const onSubmit: SubmitHandler<IForm> = async () => {
    const values = getValues();
  
    if (!values.username || values.username.trim() === "") {
      displayNotify({
        status: "error",
        title: 'Ошибка в поле "Логин"',
        description: "Поле логина обязательно для заполнения",
      });
      return;
    }
  
    if (!values.password || values.password.trim() === "") {
      displayNotify({
        status: "error",
        title: 'Ошибка в поле "Пароль"',
        description: "Поле пароля обязательно для заполнения",
      });
      return;
    }
  
    // Если всё ок — можно запускать триггер для доп. валидаций
    const valid = await trigger();
  
    if (!valid) {
      displayNotify({
        status: "error",
        title: "Ошибка валидации",
        description: "Проверьте корректность введённых данных.",
      });
      return;
    }
  
    loginM.mutate(values);
  };
  

  const onRegisterClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsRegister(true);
  };

  return (
    <div
      className="login-page"
      style={{ position: "relative", minHeight: "100vh", overflow: "hidden" }}
    >
      <div
        className="login-container"
        style={{ position: "relative", zIndex: 1 }}
      >
        <Form
          noValidate
          className="login_form"
          onSubmit={handleSubmit(onSubmit)}
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            border: "1px solid #29292A",
            borderRadius: "20px",
          }}
        >
          <div className="login_header">
            <img className="login_logo" src="/skull.svg" alt="logo" />
            <h2 className="login_title">Доброго дня, комрад!</h2>
            <p className="login_subtitle">Введите данные для входа в систему</p>
          </div>

          <div className="login_fields">
            <div className="login_input-wrapper">
              <Input
                type="text"
                placeholder="Логин"
                autoComplete="username"
                className="login_input"
                {...register("username")}
              />
            </div>

            <div className="login_input-wrapper">
              <Input
                type="password"
                placeholder="Пароль"
                autoComplete="password"
                className="login_input"
                {...register("password")}
              />
            </div>
          </div>

          <div className="login_actions">
            <Button
              contraststyle="true"
              type="submit"
              disabled={loginM.isPending}
              className="login_submit"
            >
              {loginM.isPending ? "Входим..." : "Продолжить"}
            </Button>
            <p className="forgot-password">Нет аккаунта ? - <a href="#" onClick={onRegisterClick}>
              Зарегистрироваться
            </a></p>
          </div>
        </Form>
      </div>
    </div>
  );
}

export default LoginForm;
